import { DIRECTION_VECTORS, mustParse, step, toCellId, isDiagonal } from './geometry.ts';
import { indexMap } from './map.ts';
import { check, Draft, RuleViolation } from './draft.ts';
import { block } from './reasons.ts';
import {
  DIRECTIONS,
  WEATHERS,
  type Action,
  type ApplyResult,
  type GameEvent,
  type GameState,
  type ShipState,
  type Side,
  type VolleyShooter,
} from './types.ts';
import {
  activePortFort,
  endBlock,
  entryBlock,
  otherSide,
  portMoveBlock,
  shipsAt,
  shooterBlock,
  targetBlock,
  windMoveBlock,
} from './world.ts';

/** Actions either side may send at any time (R-TURN-08). */
const ANY_TIME: ReadonlySet<Action['type']> = new Set(['resign', 'offerDraw', 'acceptDraw']);

/**
 * Applies one action. Never throws for rule violations: returns `{ ok: false, error }` with the
 * violated rule ID and reason instead. The input state is not modified.
 */
export function applyAction(state: GameState, action: Action): ApplyResult {
  try {
    const { state: next, events } = applyActionOrThrow(state, action);
    return { ok: true, state: next, events };
  } catch (e) {
    if (e instanceof RuleViolation) {
      return { ok: false, error: { rule: e.rule, reason: e.reason, message: e.message } };
    }
    throw e;
  }
}

/** Like `applyAction`, but throws `RuleViolation` on illegal actions. */
export function applyActionOrThrow(
  state: GameState,
  action: Action,
): { state: GameState; events: GameEvent[] } {
  const d = new Draft(state);
  check(d.playing ? null : block('R-END-05', 'gameOver'), 'The game is over');
  // Actions may come from untrusted clients: validate the side at runtime.
  const side: string = action.side;
  check(side === 'red' || side === 'blue' ? null : block('R-TURN-02', 'invalidAction'), 'Unknown side');
  if (!ANY_TIME.has(action.type)) {
    check(
      action.side === d.turn.side ? null : block('R-TURN-08', 'notYourTurn'),
      `It is ${d.turn.side}'s turn`,
    );
  }
  switch (action.type) {
    case 'declareMoveGroup':
      declareMoveGroup(d, action.side, action.shipIds);
      break;
    case 'spinForMovement':
      spinForMovement(d);
      break;
    case 'moveShip':
      moveShip(d, action.shipId, action.to);
      break;
    case 'moveInPort':
      moveInPort(d, action.shipId, action.to);
      break;
    case 'fire':
      fire(d, action.side, [{ id: action.shooterId, shots: 1 }], action.targetId, false);
      break;
    case 'volley':
      check(
        new Set(action.shooters.map((s) => s.id)).size === action.shooters.length &&
          action.shooters.length >= d.ruleset.volleyMinShooters
          ? null
          : block('R-VOLLEY-01', 'volleyTooFewShooters'),
        `A volley needs at least ${d.ruleset.volleyMinShooters} distinct shooters`,
      );
      fire(d, action.side, action.shooters, action.targetId, true);
      break;
    case 'enterPortRepair':
      enterPortRepair(d, action.side, action.shipId);
      break;
    case 'startFortRepair':
      startFortRepair(d, action.side, action.fortId, action.crew);
      break;
    case 'stopFortRepair': {
      const fort = d.fort(action.fortId);
      check(
        fort.repair?.side === action.side ? null : block('R-FREP-02', 'noRepairHere'),
        'No repair of yours here',
      );
      d.cancelFortRepair(fort, 'voluntary');
      break;
    }
    case 'endTurn':
      endTurn(d);
      break;
    case 'resign':
      d.emit({ type: 'resigned', side: action.side });
      d.end({ kind: 'won', winner: otherSide(action.side), reason: 'resignation' });
      break;
    case 'offerDraw':
      d.drawOffer = { by: action.side, onTurn: d.turn.number };
      d.emit({ type: 'drawOffered', side: action.side });
      break;
    case 'acceptDraw':
      check(
        d.drawOffer && d.drawOffer.by !== action.side ? null : block('R-END-04', 'noDrawOffer'),
        'There is no draw offer from the opponent',
      );
      d.drawOffer = null;
      d.end({ kind: 'draw' });
      break;
    default:
      throw new RuleViolation('R-TURN-02', 'invalidAction', `Unknown action type`);
  }
  return { state: d.finish(), events: d.events };
}

// ---------------------------------------------------------------------------
// Movement
// ---------------------------------------------------------------------------

/** R-TURN-03 */
function declareMoveGroup(d: Draft, side: Side, shipIds: readonly string[]): void {
  check(
    d.turn.phase === 'open' ? null : block('R-TURN-03', 'groupAlreadyCast'),
    'A group was already declared this turn',
  );
  check(
    shipIds.length > 0 && new Set(shipIds).size === shipIds.length
      ? null
      : block('R-TURN-03', 'invalidGroup'),
    'The group must be a non-empty set of ships',
  );
  for (const id of shipIds) {
    const ship = d.ship(id);
    check(ship.side === side ? null : block('R-TURN-03', 'notYourUnit'), `${id} is not your ship`);
    check(ship.sunk ? block('R-TURN-03', 'sunk') : null, `${id} is sunk`);
    check(ship.dockedOnTurn !== null ? block('R-SREP-02', 'inDock') : null, `${id} is in dock`);
    check(
      ship.repairingFort !== null ? block('R-FREP-02', 'onFortRepair') : null,
      `${id} is repairing a fort`,
    );
    check(ship.moved ? block('R-PORT-03', 'alreadyMoved') : null, `${id} already moved this turn`);
    check(
      ship.firedInPort ? block('R-PORT-04', 'firedInPort') : null,
      `${id} fired from a port cell this turn`,
    );
  }
  for (const id of shipIds) d.ship(id).inGroup = true;
  d.turn.group = [...shipIds];
  d.turn.phase = 'declared';
  d.emit({ type: 'groupDeclared', side, shipIds: [...shipIds] });
}

/** R-TURN-04, R-SPIN-01 */
function spinForMovement(d: Draft): void {
  check(
    d.turn.phase === 'declared' ? null : block('R-TURN-04', 'noSquadron'),
    'Declare a group before spinning',
  );
  const outcome = d.rng.int(24);
  const result = {
    direction: DIRECTIONS[Math.floor(outcome / 3)] ?? 'N',
    weather: WEATHERS[outcome % 3] ?? 'fresh',
  };
  d.turn.spin = result;
  d.turn.phase = 'spun';
  d.emit({ type: 'spun', side: d.turn.side, result });
  if (result.weather === 'storm') stormDrift(d);
}

/** R-STORM-01..07 */
function stormDrift(d: Draft): void {
  const spin = d.turn.spin;
  if (!spin) return;
  const dir = spin.direction;
  const v = DIRECTION_VECTORS[dir];
  const idx = indexMap(d.map);
  const projection = (s: ShipState) => {
    const p = mustParse(s.cell);
    return p.x * v.x + p.y * v.y;
  };
  const group = d.turn.group
    .map((id) => d.ship(id))
    .sort((a, b) => projection(b) - projection(a) || (a.id < b.id ? -1 : 1));
  for (const ship of group) {
    ship.moved = true;
    if (ship.sunk || !d.playing) continue;
    const from = ship.cell;
    let cur = mustParse(from);
    let wrecked = false;
    const drift = d.ruleset.shipClasses[ship.cls].stormDrift;
    for (let i = 0; i < drift; i++) {
      const next = step(cur, dir);
      const hard = (p: { x: number; y: number }) => !idx.inBounds(p) || idx.type(p) === 'land';
      const squeezed =
        !d.ruleset.diagonalSqueeze &&
        isDiagonal(dir) &&
        hard({ x: next.x, y: cur.y }) &&
        hard({ x: cur.x, y: next.y });
      if (hard(next) || squeezed) {
        // R-STORM-04
        ship.cell = toCellId(cur);
        d.emit({ type: 'shipAnchored', shipId: ship.id, cell: ship.cell });
        break;
      }
      if (idx.type(next) === 'reef') {
        // R-STORM-03
        if (toCellId(cur) !== from) {
          d.emit({ type: 'shipMoved', shipId: ship.id, from, to: toCellId(cur), mode: 'storm' });
        }
        ship.cell = toCellId(next);
        d.sink(ship, 'wreck');
        wrecked = true;
        break;
      }
      // R-STORM-05: full cell, defended enemy port, own Royal Port.
      if (entryBlock(d, ship, next) !== null || endBlock(d, ship, next) !== null) break;
      cur = next;
    }
    if (!wrecked) {
      ship.cell = toCellId(cur);
      if (ship.cell !== from) {
        d.emit({ type: 'shipMoved', shipId: ship.id, from, to: ship.cell, mode: 'storm' });
      }
    }
  }
}

/** R-MOVE-01..11 */
function moveShip(d: Draft, shipId: string, to: string): void {
  const ship = d.ship(shipId);
  check(ship.side === d.turn.side ? null : block('R-TURN-08', 'notYourUnit'), `${shipId} is not your ship`);
  check(windMoveBlock(d, ship, to), `${shipId} cannot move to ${to}`);
  const from = ship.cell;
  ship.cell = to;
  ship.moved = true;
  d.emit({
    type: 'shipMoved',
    shipId,
    from,
    to,
    mode: d.turn.spin?.weather === 'calm' ? 'calm' : 'wind',
  });
}

/** R-PORT-03 */
function moveInPort(d: Draft, shipId: string, to: string): void {
  const ship = d.ship(shipId);
  check(ship.side === d.turn.side ? null : block('R-TURN-08', 'notYourUnit'), `${shipId} is not your ship`);
  check(portMoveBlock(d, ship, to), `${shipId} cannot move inside the port to ${to}`);
  const from = ship.cell;
  ship.cell = to;
  ship.moved = true;
  d.emit({ type: 'shipMoved', shipId, from, to, mode: 'port' });
}

// ---------------------------------------------------------------------------
// Fire
// ---------------------------------------------------------------------------

/** R-FIRE-*, R-VOLLEY-* */
function fire(
  d: Draft,
  side: Side,
  shooters: readonly VolleyShooter[],
  targetId: string,
  volley: boolean,
): void {
  for (const s of shooters) {
    check(
      Number.isInteger(s.shots) && s.shots >= 1 ? null : block('R-VOLLEY-02', 'invalidShots'),
      'Each shooter commits at least one shot',
    );
    check(shooterBlock(d, side, s.id, s.shots), `${s.id} cannot fire ${s.shots} shot(s)`);
    check(targetBlock(d, s.id, targetId), `${s.id} cannot target ${targetId}`);
  }
  let total = 0;
  for (const s of shooters) {
    total += s.shots;
    const ship = d.ships.find((x) => x.id === s.id);
    if (ship) {
      ship.shotsLeft -= s.shots;
      ship.fired = true;
      if (d.isActivePortCell(ship.cell)) ship.firedInPort = true;
    } else {
      const fort = d.fort(s.id);
      fort.shotsLeft -= s.shots;
      fort.fired = true;
    }
  }
  const hit = d.rng.float() < d.ruleset.hitChance;
  d.emit({ type: 'shot', shooters: shooters.map((s) => ({ ...s })), targetId, volley, hit });
  if (!hit) return;
  const ship = d.ships.find((x) => x.id === targetId);
  if (ship) d.damageShip(ship, total);
  else d.hitFort(d.fort(targetId), total);
}

// ---------------------------------------------------------------------------
// Repairs
// ---------------------------------------------------------------------------

/** R-SREP-01 */
function enterPortRepair(d: Draft, side: Side, shipId: string): void {
  const ship = d.ship(shipId);
  check(
    ship.side === side && !ship.sunk ? null : block('R-SREP-01', 'notYourUnit'),
    `${shipId} is not your ship`,
  );
  check(
    ship.dockedOnTurn === null ? null : block('R-SREP-01', 'alreadyInDock'),
    `${shipId} is already in dock`,
  );
  check(
    ship.repairingFort === null ? null : block('R-FREP-02', 'onFortRepair'),
    `${shipId} is repairing a fort`,
  );
  check(ship.damage > 0 ? null : block('R-SREP-01', 'notDamaged'), `${shipId} is not damaged`);
  check(ship.fired ? block('R-SREP-01', 'firedThisTurn') : null, `${shipId} fired this turn`);
  const fort = activePortFort(d, mustParse(ship.cell));
  check(
    fort?.side === side ? null : block('R-SREP-01', 'notInOwnPort'),
    `${shipId} is not in an own defended port`,
  );
  ship.dockedOnTurn = d.turn.number;
  ship.moved = true;
  d.emit({ type: 'dockEntered', shipId });
}

/** R-FREP-01, R-FREP-04 */
function startFortRepair(d: Draft, side: Side, fortId: string, crew: readonly string[]): void {
  const fort = d.fort(fortId);
  check(
    fort.repair === null ? null : block('R-FREP-01', 'alreadyRepairing'),
    `${fortId} is already being repaired`,
  );
  if (fort.destroyed) {
    const enemies = d.ships.some((s) => !s.sunk && s.side !== side && fort.portCells.includes(s.cell));
    check(enemies ? block('R-FREP-04', 'enemiesInPort') : null, 'Enemy ships are in the port');
  } else {
    check(fort.side === side ? null : block('R-FREP-01', 'notYourFort'), `${fortId} is not your fort`);
    check(fort.hits > 0 ? null : block('R-FREP-01', 'notDamaged'), `${fortId} is not damaged`);
  }
  check(
    crew.length === d.ruleset.fortRepairCrew && new Set(crew).size === crew.length
      ? null
      : block('R-FREP-01', 'wrongCrewSize'),
    `The crew must be exactly ${d.ruleset.fortRepairCrew} distinct ships`,
  );
  for (const id of crew) {
    const ship = d.ship(id);
    check(
      ship.side === side && !ship.sunk ? null : block('R-FREP-01', 'notYourUnit'),
      `${id} is not your ship`,
    );
    check(
      fort.portCells.includes(ship.cell) ? null : block('R-FREP-01', 'crewNotInPort'),
      `${id} is not in the port`,
    );
    check(ship.dockedOnTurn === null ? null : block('R-FREP-01', 'inDock'), `${id} is in dock`);
    check(
      ship.repairingFort === null ? null : block('R-FREP-01', 'alreadyRepairing'),
      `${id} is already repairing`,
    );
    check(ship.fired ? block('R-FREP-01', 'firedThisTurn') : null, `${id} fired this turn`);
  }
  for (const id of crew) d.ship(id).repairingFort = fortId;
  fort.repair = { side, crew: [...crew], startedOnTurn: d.turn.number };
  d.emit({ type: 'fortRepairStarted', fortId, side, crew: [...crew] });
}

// ---------------------------------------------------------------------------
// End of turn
// ---------------------------------------------------------------------------

/** R-TURN-07 */
function endTurn(d: Draft): void {
  check(
    d.turn.phase === 'declared' ? block('R-TURN-04', 'castWindFirst') : null,
    'Spin for the declared group first',
  );
  const side = d.turn.side;
  const turn = d.turn.number;

  // R-SREP-03
  for (const ship of d.ships) {
    if (ship.side !== side || ship.sunk || ship.dockedOnTurn === null) continue;
    if (ship.dockedOnTurn >= turn) continue;
    ship.damage = Math.max(0, ship.damage - 1);
    d.emit({ type: 'shipRepaired', shipId: ship.id, damage: ship.damage });
    if (ship.damage === 0) {
      ship.dockedOnTurn = null;
      d.emit({ type: 'dockLeft', shipId: ship.id });
    }
  }

  // R-FREP-03, R-FREP-05, R-FREP-06
  for (const fort of d.forts) {
    const repair = fort.repair;
    if (!repair || repair.side !== side || repair.startedOnTurn >= turn) continue;
    if (fort.destroyed) {
      const enemies = d.ships.some((s) => !s.sunk && s.side !== side && fort.portCells.includes(s.cell));
      if (enemies) continue;
    } else if (fort.fired) {
      continue;
    }
    fort.hits = Math.max(0, fort.hits - d.ruleset.fortRepairPerTurn);
    d.emit({ type: 'fortRepaired', fortId: fort.id, hits: fort.hits });
    if (fort.hits === 0) {
      if (fort.destroyed) {
        fort.destroyed = false;
        fort.side = repair.side;
        d.emit({ type: 'fortCaptured', fortId: fort.id, side: repair.side });
      }
      d.cancelFortRepair(fort, 'completed');
    }
  }

  // R-ROYAL-04
  const royal = indexMap(d.map).royalCell(otherSide(side));
  if (royal) {
    const present = shipsAt(d, royal)
      .filter((s) => s.side === side)
      .map((s) => s.id);
    const held = present.some((id) => d.turn.royalAtStart.includes(id));
    const before = d.holdCount[side];
    d.holdCount[side] = held ? before + 1 : 0;
    if (held || before > 0) {
      d.emit({ type: 'royalHold', side, count: d.holdCount[side] });
    }
    if (d.holdCount[side] >= d.ruleset.royalPortHoldTurns) {
      d.end({ kind: 'won', winner: side, reason: 'royal_port' });
    }
  }

  // R-END-04: an offer lapses when the opponent's turn ends.
  if (d.drawOffer && d.drawOffer.by !== side) {
    d.emit({ type: 'drawOfferExpired', side: d.drawOffer.by });
    d.drawOffer = null;
  }

  if (d.playing) d.startTurn(turn + 1, otherSide(side));
}
